import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getLocalizedApiMessage,
  showApiErrorToast,
  showApiSuccessToast,
} from "../../../utils/apiMessageHelper";
import {
  getEmailDeliveryTypes,
  getFormatNameSeeder,
  getGeneralSettings,
  getPasswordPolicy,
  getPasswordReminders,
  getSmtpEncryptions,
  updateGeneralSettingsRequest,
  updatePasswordPolicyRequest,
} from "../../../services/api/generalSettings/generalSettingsApi";
import {
  fetchGeneralSettingsOverOtherScheme,
  syncHttpAccessFromSettings,
} from "../../../services/httpAccess";

const GENERAL_SETTINGS_UPDATE_FALLBACK = {
  en: "General settings updated successfully!",
  ar: "تم تحديث الإعدادات العامة بنجاح!",
};

const GENERAL_SETTINGS_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update general settings.",
  ar: "فشل في تحديث الإعدادات العامة.",
};

const PASSWORD_POLICY_UPDATE_FALLBACK = {
  en: "Password policy updated successfully!",
  ar: "تم تحديث سياسة كلمة المرور بنجاح!",
};

const PASSWORD_POLICY_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update password policy.",
  ar: "فشل في تحديث سياسة كلمة المرور.",
};

export const fetchGeneralSettings = createAsyncThunk(
  "generalSettings/fetchGeneralSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getGeneralSettings();
      syncHttpAccessFromSettings(response.data.data);
      return response.data.data;
    } catch (error) {
      if (!error.response) {
        const fallback = await fetchGeneralSettingsOverOtherScheme();
        if (fallback) {
          syncHttpAccessFromSettings(fallback);
          return fallback;
        }
      }
      return rejectWithValue("Failed to fetch general settings");
    }
  },
);

export const updateGeneralSettings = createAsyncThunk(
  "generalSettings/updateGeneralSettings",
  async (settings, { rejectWithValue }) => {
    try {
      const response = await updateGeneralSettingsRequest(settings);
      syncHttpAccessFromSettings(response.data.data);
      return {
        data: response.data.data,
        apiMessage: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, GENERAL_SETTINGS_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

export const fetchPasswordPolicy = createAsyncThunk(
  "generalSettings/fetchPasswordPolicy",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPasswordPolicy();
      return response?.data?.data ?? response?.data ?? null;
    } catch (error) {
      return rejectWithValue("Failed to fetch password policy");
    }
  },
);

export const updatePasswordPolicy = createAsyncThunk(
  "generalSettings/updatePasswordPolicy",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updatePasswordPolicyRequest(payload);
      return {
        data: response?.data?.data ?? response?.data ?? payload,
        apiMessage: response?.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, PASSWORD_POLICY_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

export const fetchSmtpEncryption = createAsyncThunk(
  "generalSettings/fetchSmtpEncryption",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSmtpEncryptions();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch SMTP encryptions");
    }
  },
);

export const fetchEmailDeliveryTypes = createAsyncThunk(
  "generalSettings/fetchEmailDeliveryTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getEmailDeliveryTypes();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch email delivery types");
    }
  },
);

export const fetchPasswordReminders = createAsyncThunk(
  "generalSettings/fetchPasswordReminders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPasswordReminders();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch password reminders");
    }
  },
);

export const fetchFormatNameSeeder = createAsyncThunk(
  "generalSettings/fetchFormatNameSeeder",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getFormatNameSeeder();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch format name seeder");
    }
  },
);

const generalSettingsSlice = createSlice({
  name: "generalSettings",
  initialState: {
    settings: null,
    workflow_theme_logo: null,
    page_not_found_image: null,
    passwordPolicy: null,
    smtpEncryptions: [],
    emailDeliveryTypes: [],
    passwordReminders: [],
    formatNameSeeder: [],
    loading: false,
    error: null,
  },
  reducers: {
    updateField: (state, action) => {
      const { name, value } = action.payload;
      if (state.settings && state.settings[name] !== undefined) {
        state.settings[name] = value;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGeneralSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGeneralSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
        if (action.payload) {
          state.workflow_theme_logo = action.payload.workflow_theme_logo || null;
          state.page_not_found_image =
            action.payload.page_not_found_image || null;
        }
      })
      .addCase(fetchGeneralSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateGeneralSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGeneralSettings.fulfilled, (state, action) => {
        state.loading = false;
        const updatedSettings = action.payload?.data ?? action.payload;
        state.settings = updatedSettings;
        if (updatedSettings) {
          state.workflow_theme_logo =
            updatedSettings.workflow_theme_logo || null;
          state.page_not_found_image =
            updatedSettings.page_not_found_image || null;
        }
        showApiSuccessToast(
          action.payload?.apiMessage,
          GENERAL_SETTINGS_UPDATE_FALLBACK,
        );
      })
      .addCase(updateGeneralSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showApiErrorToast(
          action.payload,
          GENERAL_SETTINGS_UPDATE_ERROR_FALLBACK,
        );
      })
      .addCase(fetchPasswordPolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPasswordPolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordPolicy = action.payload;
      })
      .addCase(fetchPasswordPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePasswordPolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePasswordPolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordPolicy = action.payload?.data ?? action.payload;
        showApiSuccessToast(
          action.payload?.apiMessage,
          PASSWORD_POLICY_UPDATE_FALLBACK,
        );
      })
      .addCase(updatePasswordPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showApiErrorToast(
          action.payload,
          PASSWORD_POLICY_UPDATE_ERROR_FALLBACK,
        );
      })
      .addCase(fetchSmtpEncryption.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSmtpEncryption.fulfilled, (state, action) => {
        state.loading = false;
        state.smtpEncryptions = action.payload || [];
      })
      .addCase(fetchSmtpEncryption.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchEmailDeliveryTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmailDeliveryTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.emailDeliveryTypes = action.payload || [];
      })
      .addCase(fetchEmailDeliveryTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPasswordReminders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPasswordReminders.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordReminders = action.payload || [];
      })
      .addCase(fetchPasswordReminders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchFormatNameSeeder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormatNameSeeder.fulfilled, (state, action) => {
        state.loading = false;
        state.formatNameSeeder = action.payload || [];
      })
      .addCase(fetchFormatNameSeeder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { updateField } = generalSettingsSlice.actions;
export default generalSettingsSlice.reducer;
